import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, User, Lock, ArrowRight, Check, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function Setup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  const [companyData, setCompanyData] = useState({
    name: '',
    tax_number: '',
    address: ''
  });
  
  const [adminData, setAdminData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirm: ''
  });

  useEffect(() => {
    checkSetupRequired();
  }, []);

  const checkSetupRequired = async () => {
    try {
      const res = await api.get('/setup/check');
      if (!res.data.setup_required) {
        navigate('/login');
      }
    } catch (err) {
      console.error('Setup check failed:', err);
    }
    setLoading(false);
  };

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (!companyData.name.trim()) {
      toast.error('Sirket adi zorunludur');
      return;
    }
    setStep(2);
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    
    if (!adminData.name.trim()) {
      toast.error('Admin adi zorunludur');
      return;
    }
    if (!adminData.email.trim()) {
      toast.error('E-posta adresi zorunludur');
      return;
    }
    if (!adminData.password || adminData.password.length < 6) {
      toast.error('Sifre en az 6 karakter olmalidir');
      return;
    }
    if (adminData.password !== adminData.password_confirm) {
      toast.error('Sifreler eslesmiyor');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/setup/init', {
        company_name: companyData.name,
        company_tax_number: companyData.tax_number,
        company_address: companyData.address,
        admin_name: adminData.name,
        admin_email: adminData.email,
        admin_password: adminData.password
      });

      if (res.data.success) {
        // Store token and user data
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('selectedCompany', JSON.stringify(res.data.company));
        
        toast.success('Sistem basariyla kuruldu! Hosgeldiniz.');
        
        // Redirect to dashboard
        setTimeout(() => {
          window.location.href = '/erp';
        }, 1000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kurulum sirasinda hata olustu');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f0e8] to-[#e8dfd0] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2e2d29]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f0e8] to-[#e8dfd0] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2e2d29] text-[#f5f0e8] mb-4">
            <Shield size={32} />
          </div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">TorosERP Kurulum</h1>
          <p className="text-[#8a7e70] mt-2">Sisteminizi kurmak icin asagidaki adimlari tamamlayin</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#2e2d29]' : 'text-[#8a7e70]'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'bg-[#d0c4b0] text-[#8a7e70]'}`}>
              {step > 1 ? <Check size={16} /> : '1'}
            </div>
            <span className="font-medium">Sirket</span>
          </div>
          <ArrowRight size={16} className="text-[#d0c4b0]" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#2e2d29]' : 'text-[#8a7e70]'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'bg-[#d0c4b0] text-[#8a7e70]'}`}>
              2
            </div>
            <span className="font-medium">Admin</span>
          </div>
        </div>

        {/* Step 1: Company Info */}
        {step === 1 && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-lg" data-testid="setup-company-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916] flex items-center gap-2">
                <Building2 size={20} /> Sirket Bilgileri
              </CardTitle>
              <CardDescription className="text-[#8a7e70]">
                Sirketinizin temel bilgilerini girin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCompanySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Sirket Adi *</Label>
                  <Input
                    value={companyData.name}
                    onChange={(e) => setCompanyData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Ornek Sirket A.S."
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-company-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Vergi Numarasi</Label>
                  <Input
                    value={companyData.tax_number}
                    onChange={(e) => setCompanyData(p => ({ ...p, tax_number: e.target.value }))}
                    placeholder="1234567890"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-company-tax"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Adres</Label>
                  <Textarea
                    value={companyData.address}
                    onChange={(e) => setCompanyData(p => ({ ...p, address: e.target.value }))}
                    placeholder="Sirket adresi"
                    className="bg-white border-[#d0c4b0] min-h-[80px]"
                    data-testid="setup-company-address"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
                  data-testid="setup-next-btn"
                >
                  Devam Et
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Admin User */}
        {step === 2 && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-lg" data-testid="setup-admin-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916] flex items-center gap-2">
                <User size={20} /> Admin Kullanici
              </CardTitle>
              <CardDescription className="text-[#8a7e70]">
                Sistem yoneticisi hesabini olusturun
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Ad Soyad *</Label>
                  <Input
                    value={adminData.name}
                    onChange={(e) => setAdminData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Admin Kullanici"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-admin-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">E-posta *</Label>
                  <Input
                    type="email"
                    value={adminData.email}
                    onChange={(e) => setAdminData(p => ({ ...p, email: e.target.value }))}
                    placeholder="admin@sirket.com"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-admin-email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Sifre *</Label>
                  <Input
                    type="password"
                    value={adminData.password}
                    onChange={(e) => setAdminData(p => ({ ...p, password: e.target.value }))}
                    placeholder="En az 6 karakter"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-admin-password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Sifre Tekrar *</Label>
                  <Input
                    type="password"
                    value={adminData.password_confirm}
                    onChange={(e) => setAdminData(p => ({ ...p, password_confirm: e.target.value }))}
                    placeholder="Sifreyi tekrar girin"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="setup-admin-password-confirm"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 border-[#d0c4b0]"
                    data-testid="setup-back-btn"
                  >
                    Geri
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] font-semibold"
                    data-testid="setup-complete-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Kuruluyor...
                      </>
                    ) : (
                      <>
                        <Check size={16} className="mr-2" />
                        Kurulumu Tamamla
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-[#8a7e70] mt-6">
          TorosERP v1.0 - Ilk kurulum sihirbazi
        </p>
      </div>
    </div>
  );
}
